import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, TrendingUp, Coins, Plus, Pencil, ShoppingCart, ArrowDownToLine, ArrowUpFromLine, Receipt, Car, Trash2, Hourglass, ArrowDownLeft, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, todayISO } from '../lib/format';
import PageHeader from '../components/PageHeader';
import KpiCard from '../components/KpiCard';
import VehicleStatusBadge from '../components/VehicleStatusBadge';
import WithdrawalDialog from '../components/WithdrawalDialog';
import ManualEntryDialog from '../components/ManualEntryDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const TX_LABELS = {
  SALE_CREDIT: { label: 'Venda', icon: ShoppingCart, color: 'text-[hsl(var(--success))]', sign: '+' },
  VEHICLE_APORTE: { label: 'Aporte', icon: Car, color: 'text-[hsl(var(--danger))]', sign: '-' },
  WITHDRAWAL: { label: 'Saque', icon: ArrowUpFromLine, color: 'text-[hsl(var(--danger))]', sign: '-' },
  MANUAL_CREDIT: { label: 'Crédito manual', icon: ArrowDownToLine, color: 'text-[hsl(var(--success))]', sign: '+' },
  MANUAL_DEBIT: { label: 'Débito manual', icon: ArrowUpFromLine, color: 'text-[hsl(var(--danger))]', sign: '-' },
};

const MANUAL_TYPES = new Set(['WITHDRAWAL', 'MANUAL_CREDIT', 'MANUAL_DEBIT']);

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statement, setStatement] = useState(null);
  const [balance, setBalance] = useState({ balance: 0, capital_in_stock: 0, to_receive: 0, to_pay: 0, balance_projected: 0 });
  const [transactions, setTransactions] = useState([]);
  const [partner, setPartner] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState({}); // Map of vehicle_id -> full vehicle data
  const [loading, setLoading] = useState(true);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statementRes, txsRes] = await Promise.all([
        api.get(`/partners/${id}/statement`),
        api.get(`/partners/${id}/transactions`),
      ]);
      setStatement(statementRes.data);
      setTransactions(txsRes.data.transactions || []);
      setPartner(txsRes.data.partner);
      
      // Fetch detailed balance with capital_in_stock + forecast
      const balRes = await api.get(`/partners/${id}/balance`);
      setBalance({
        balance: balRes.data.balance_realized ?? balRes.data.balance ?? 0,
        capital_in_stock: balRes.data.capital_in_stock || 0,
        to_receive: balRes.data.to_receive || 0,
        to_pay: balRes.data.to_pay || 0,
        balance_projected: balRes.data.balance_projected || 0,
      });

      // Fetch full vehicle details for sold vehicles to check for future payments
      const soldVehicles = (statementRes.data.items || []).filter(v => v.is_sold);
      const vehicleDetailsMap = {};
      await Promise.all(
        soldVehicles.map(async (v) => {
          try {
            const res = await api.get(`/vehicles/${v.vehicle_id}`);
            vehicleDetailsMap[v.vehicle_id] = res.data;
          } catch (err) {
            // If error, just skip
          }
        })
      );
      setVehicleDetails(vehicleDetailsMap);
    } catch (err) {
      toast.error('Erro ao carregar sócio');
      navigate('/socios');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const manualDebitsTotal = transactions
    .filter((t) => t.type === 'MANUAL_DEBIT' || t.type === 'WITHDRAWAL')
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const netResult = (statement?.totals?.total_profit || 0) - manualDebitsTotal;

  const removeTransaction = async (tx) => {
    if (!window.confirm(`Excluir lançamento "${tx.description}"?`)) return;
    try {
      await api.delete(`/partner-transactions/${tx.id}`);
      toast.success('Lançamento excluído');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Helper: Split profit into realized vs forecast
  const splitProfitByPayments = (vehicleId, totalProfit, totalToReceive) => {
    const vehicle = vehicleDetails[vehicleId];
    if (!vehicle || !vehicle.sale_payments || totalProfit === null) {
      return { realized: totalProfit, forecast: 0, hasForecast: false };
    }
    
    const today = todayISO();
    const payments = vehicle.sale_payments || [];
    
    let totalCash = 0;
    let realizedCash = 0;
    
    for (const p of payments) {
      if (p.method === 'TRADE') continue; // Trades não são caixa
      const amt = Math.abs(p.amount || 0);
      totalCash += amt;
      if ((p.date || '') <= today) {
        realizedCash += amt;
      }
    }
    
    if (totalCash === 0) {
      return { realized: totalProfit, forecast: 0, hasForecast: false };
    }
    
    const realizedRatio = realizedCash / totalCash;
    const realized = Math.round(totalProfit * realizedRatio * 100) / 100;
    const forecast = Math.round((totalProfit - realized) * 100) / 100;
    
    return { 
      realized, 
      forecast, 
      hasForecast: forecast > 0.01,
      realizedRatio: Math.round(realizedRatio * 100)
    };
  };

  if (loading || !statement) {
    return <Skeleton className="h-64 rounded-xl" />;
  }

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/socios')} className="-ml-2" data-testid="partner-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar para Sócios
        </Button>
      </div>
      <PageHeader
        title={partner.name}
        subtitle={partner.email || 'Conta-corrente, extrato e participações.'}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowWithdrawal(true)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="partner-withdrawal-button">
              <ArrowUpFromLine className="h-4 w-4 mr-1.5" /> Registrar saque
            </Button>
            <Button variant="outline" onClick={() => setShowManual(true)} data-testid="partner-manual-entry-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Lançamento manual
            </Button>
          </div>
        }
      />

      {/* Saldo + Capital */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <KpiCard
          label="Saldo em conta-corrente"
          value={formatBRL(balance.balance)}
          icon={Wallet}
          intent={balance.balance >= 0 ? 'success' : 'danger'}
          sublabel={balance.balance >= 0 ? 'realizado · disponível para saque' : 'adiantamento (saldo negativo)'}
          testid="partner-kpi-balance"
        />
        <KpiCard
          label="Capital em estoque"
          value={formatBRL(balance.capital_in_stock)}
          icon={Car}
          sublabel="investido em veículos ativos"
          testid="partner-kpi-stock"
        />
        <KpiCard
          label="Lucro acumulado (vendas)"
          value={formatBRL(statement.totals.total_profit)}
          icon={TrendingUp}
          intent={statement.totals.total_profit >= 0 ? 'success' : 'danger'}
          testid="partner-kpi-profit"
        />
        <KpiCard
          label="Resultado líquido"
          value={formatBRL(netResult)}
          icon={Receipt}
          intent={netResult >= 0 ? 'success' : 'danger'}
          sublabel={`lucro − ${formatBRL(manualDebitsTotal)} em débitos e saques`}
          testid="partner-kpi-net-result"
        />
      </div>

      {/* Fluxo de caixa previsto */}
      {(balance.to_receive > 0 || balance.to_pay > 0) && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Hourglass className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Fluxo de caixa previsto</h2>
            <span className="text-xs text-muted-foreground">Lançamentos com data futura</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              label="A receber"
              value={formatBRL(balance.to_receive)}
              icon={ArrowDownLeft}
              intent={balance.to_receive > 0 ? 'success' : 'default'}
              sublabel="vendas e créditos futuros"
              testid="partner-kpi-to-receive"
            />
            <KpiCard
              label="Saques / débitos agendados"
              value={formatBRL(balance.to_pay)}
              icon={ArrowUpFromLine}
              intent={balance.to_pay > 0 ? 'danger' : 'default'}
              sublabel="saídas previstas"
              testid="partner-kpi-to-pay"
            />
            <KpiCard
              label="Saldo projetado"
              value={formatBRL(balance.balance_projected)}
              icon={TrendingUp}
              intent={balance.balance_projected >= 0 ? 'success' : 'danger'}
              sublabel={`Atual ${formatBRL(balance.balance)} + previstos`}
              testid="partner-kpi-projected"
            />
          </div>
        </div>
      )}

      <Tabs defaultValue="movimentacoes">
        <TabsList className="grid grid-cols-2 w-full max-w-md h-auto">
          <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Receipt className="h-4 w-4 shrink-0" /> <span>Movimentações</span>
          </TabsTrigger>
          <TabsTrigger value="veiculos" data-testid="tab-veiculos" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Car className="h-4 w-4 shrink-0" /> <span>Veículos</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="movimentacoes" className="mt-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Extrato da conta-corrente</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Nenhuma movimentação ainda. O saldo é creditado automaticamente quando os veículos onde este sócio participa são vendidos.
                </div>
              ) : (
                <>
                  {/* Desktop: table */}
                  <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border/70">
                          <th className="text-left py-2 pr-3 font-medium">Data</th>
                          <th className="text-left py-2 px-3 font-medium">Tipo</th>
                          <th className="text-left py-2 px-3 font-medium">Descrição</th>
                          <th className="text-right py-2 px-3 font-medium">Valor</th>
                          <th className="text-right py-2 px-3 font-medium">Saldo após</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/70">
                        {(() => {
                          let runningBalance = 0;
                          return transactions.map((tx) => {
                            const cfg = TX_LABELS[tx.type] || { label: tx.type, icon: Receipt, color: '', sign: '' };
                            const Icon = cfg.icon;
                            const canDelete = MANUAL_TYPES.has(tx.type);
                            const isForecast = !!tx.is_forecast;
                            if (!isForecast) {
                              if (cfg.sign === '+') runningBalance += tx.amount;
                              else runningBalance -= tx.amount;
                            }
                            const balanceForRow = runningBalance;
                            return (
                              <tr
                                key={tx.id}
                                className={`hover:bg-muted/30 ${isForecast ? 'bg-amber-50/50' : ''}`}
                                data-testid="partner-transaction-row"
                              >
                                <td className="py-3 pr-3 tabular text-muted-foreground">{formatDateBR(tx.date)}</td>
                                <td className="py-3 px-3">
                                  <span className="inline-flex items-center gap-1.5 text-xs">
                                    <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
                                    <span>{cfg.label}</span>
                                    {isForecast && (
                                      <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200" data-testid="forecast-badge">
                                        <Hourglass className="h-2.5 w-2.5" /> Previsto
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <div className="text-sm">{tx.description}</div>
                                  {tx.related_vehicle_id && (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/veiculos/${tx.related_vehicle_id}`)}
                                      className="text-[11px] text-[hsl(var(--primary))] hover:underline"
                                    >
                                      → ver veículo
                                    </button>
                                  )}
                                </td>
                                <td className={`py-3 px-3 text-right tabular font-semibold ${isForecast ? 'text-muted-foreground italic' : cfg.color}`}>
                                  {cfg.sign}{formatBRL(tx.amount).replace('-', '').replace('R$', 'R$')}
                                </td>
                                <td className={`py-3 px-3 text-right tabular ${isForecast ? 'text-muted-foreground' : (balanceForRow >= 0 ? 'text-muted-foreground' : 'text-[hsl(var(--danger))]')}`}>
                                  {isForecast ? '—' : formatBRL(balanceForRow)}
                                </td>
                                <td className="py-3 px-3 text-right">
                                  {canDelete && (
                                    <Button variant="ghost" size="icon" onClick={() => removeTransaction(tx)} title="Excluir" data-testid="partner-tx-delete">
                                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-border bg-muted/20">
                          <td colSpan={4} className="py-3 px-3 text-right font-medium">Saldo atual</td>
                          <td className={`py-3 px-3 text-right tabular font-bold text-base ${balance.balance >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                            {formatBRL(balance.balance)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Mobile: cards */}
                  <div className="md:hidden space-y-2">
                    {(() => {
                      let rb = 0;
                      return transactions.map((tx) => {
                        const cfg = TX_LABELS[tx.type] || { label: tx.type, icon: Receipt, color: '', sign: '' };
                        const Icon = cfg.icon;
                        const canDelete = MANUAL_TYPES.has(tx.type);
                        const isForecast = !!tx.is_forecast;
                        if (!isForecast) {
                          if (cfg.sign === '+') rb += tx.amount; else rb -= tx.amount;
                        }
                        const bal = rb;
                        return (
                          <div
                            key={tx.id}
                            className={`border rounded-lg p-3 ${isForecast ? 'border-amber-300 bg-amber-50/50' : 'border-border/70'}`}
                            data-testid="partner-transaction-card"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1 flex-wrap">
                                  <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
                                  <span>{cfg.label}</span>
                                  <span>·</span>
                                  <span className="tabular">{formatDateBR(tx.date)}</span>
                                  {isForecast && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                      <Hourglass className="h-2.5 w-2.5" /> Previsto
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm">{tx.description}</div>
                                {tx.related_vehicle_id && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/veiculos/${tx.related_vehicle_id}`)}
                                    className="text-[11px] text-[hsl(var(--primary))] hover:underline mt-1"
                                  >
                                    → ver veículo
                                  </button>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <div className={`text-sm tabular font-semibold ${isForecast ? 'text-muted-foreground italic' : cfg.color}`}>
                                  {cfg.sign}{formatBRL(tx.amount).replace('-', '')}
                                </div>
                                <div className={`text-[10px] tabular ${isForecast ? 'text-muted-foreground' : (bal >= 0 ? 'text-muted-foreground' : 'text-[hsl(var(--danger))]')}`}>
                                  {isForecast ? '— previsto' : `saldo: ${formatBRL(bal)}`}
                                </div>
                              </div>
                              {canDelete && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeTransaction(tx)} title="Excluir">
                                  <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    <div className="border-t-2 border-border pt-3 flex items-center justify-between">
                      <span className="text-sm font-medium">Saldo atual</span>
                      <span className={`tabular font-bold text-base ${balance.balance >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                        {formatBRL(balance.balance)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="veiculos" className="mt-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Veículos com participação</CardTitle>
            </CardHeader>
            <CardContent>
              {statement.items.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Sem participações em veículos ainda.</div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-sm">
                      <thead className="text-muted-foreground">
                        <tr className="border-b border-border/70">
                          <th className="text-left py-2 font-medium">Veículo</th>
                          <th className="text-left py-2 font-medium">Status</th>
                          <th className="text-right py-2 font-medium">Capital investido</th>
                          <th className="text-right py-2 font-medium">Lucro / Prejuízo</th>
                          <th className="text-right py-2 font-medium">A receber</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/70">
                        {statement.items.map((it) => {
                          const profitSplit = splitProfitByPayments(it.vehicle_id, it.profit_share, it.total_to_receive);
                          return (
                            <tr key={it.vehicle_id} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/veiculos/${it.vehicle_id}`)} data-testid="partner-statement-row">
                              <td className="py-3 font-medium">
                                <div className="flex items-center gap-2">
                                  <span>{it.vehicle_label}</span>
                                  {it.plate && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border/60">
                                      {it.plate}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3"><VehicleStatusBadge status={it.status} /></td>
                              <td className="py-3 text-right tabular">{formatBRL(it.capital_invested)}</td>
                              <td className="py-3 text-right tabular">
                                {it.profit_share === null ? '—' : (
                                  <TooltipProvider>
                                    <div className="flex items-center justify-end gap-1.5">
                                      <span className={profitSplit.realized >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}>
                                        {formatBRL(profitSplit.realized, { sign: true })}
                                      </span>
                                      {profitSplit.hasForecast && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button type="button" onClick={(e) => e.stopPropagation()} className="text-amber-600 hover:text-amber-700">
                                              <Info className="h-3.5 w-3.5" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs">
                                            <div className="text-xs space-y-1">
                                              <div className="font-semibold">Lucro parcialmente previsto</div>
                                              <div>Realizado: <span className="font-medium">{formatBRL(profitSplit.realized)}</span> ({profitSplit.realizedRatio}%)</div>
                                              <div>Previsto: <span className="font-medium text-amber-700">{formatBRL(profitSplit.forecast)}</span></div>
                                              <div className="text-muted-foreground pt-1 border-t">
                                                Este veículo tem pagamentos com data futura. O lucro será totalmente realizado quando todos os pagamentos forem recebidos.
                                              </div>
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </TooltipProvider>
                                )}
                              </td>
                              <td className="py-3 text-right tabular font-semibold">{it.total_to_receive === null ? '—' : formatBRL(it.total_to_receive)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile cards */}
                  <div className="md:hidden space-y-2">
                    {statement.items.map((it) => {
                      const profitSplit = splitProfitByPayments(it.vehicle_id, it.profit_share, it.total_to_receive);
                      return (
                        <button
                          key={it.vehicle_id}
                          type="button"
                          onClick={() => navigate(`/veiculos/${it.vehicle_id}`)}
                          className="w-full text-left border border-border/70 rounded-lg p-3 hover:bg-muted/40"
                          data-testid="partner-statement-row"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{it.vehicle_label}</div>
                              {it.plate && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border/60 mt-1">
                                  {it.plate}
                                </span>
                              )}
                            </div>
                            <VehicleStatusBadge status={it.status} />
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <div className="text-muted-foreground">Capital</div>
                              <div className="tabular">{formatBRL(it.capital_invested)}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground">Lucro</div>
                              {it.profit_share === null ? (
                                <div className="tabular">—</div>
                              ) : (
                                <TooltipProvider>
                                  <div className="flex items-center gap-1">
                                    <div className={`tabular ${profitSplit.realized >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                                      {formatBRL(profitSplit.realized, { sign: true })}
                                    </div>
                                    {profitSplit.hasForecast && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button type="button" onClick={(e) => e.stopPropagation()} className="text-amber-600">
                                            <Info className="h-3 w-3" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                          <div className="text-xs space-y-1">
                                            <div className="font-semibold">Lucro parcialmente previsto</div>
                                            <div>Realizado: <span className="font-medium">{formatBRL(profitSplit.realized)}</span></div>
                                            <div>Previsto: <span className="font-medium text-amber-700">{formatBRL(profitSplit.forecast)}</span></div>
                                            <div className="text-muted-foreground pt-1 border-t text-[10px]">
                                              Pagamentos futuros pendentes.
                                            </div>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TooltipProvider>
                              )}
                            </div>
                            <div>
                              <div className="text-muted-foreground">A receber</div>
                              <div className="tabular font-semibold">{it.total_to_receive === null ? '—' : formatBRL(it.total_to_receive)}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <WithdrawalDialog
        open={showWithdrawal}
        onOpenChange={setShowWithdrawal}
        partnerId={id}
        partnerName={partner.name}
        currentBalance={balance.balance}
        onSaved={load}
      />
      <ManualEntryDialog
        open={showManual}
        onOpenChange={setShowManual}
        partnerId={id}
        partnerName={partner.name}
        onSaved={load}
      />
    </>
  );
}
